import {Core} from 'cytoscape';
import {GraphMap, GraphNodeMapListKey, readSelectedMap} from '../app/api/interfaces/graph-node.interface';
import {signal} from '@angular/core';
import {isInIframe} from '../app/shared/helpers/maporium.validators';
import {MapService} from '../app/api/services/map.service';
import {get} from 'lodash';
import {isRestoringPanZoom} from '../app/shared/helpers/signal.helpers';

export const PAN_ZOOM_STORAGE_KEY = 'pan-zoom-storage';
export const COLLAPSED_NODE_PAN_STATE_KEY = 'collapsedNodePanState';
export const GENERAL_COLLAPSED_PAN_STATE_KEY = 'generalCollapsedPanState';

export let ZOOM_IN_PROGRESS = false;
export const centeringInProgress = signal(false);
export let HOME_BUTTON_CENTERING = false;
export let IS_PAN_ZOOM_DESTROYED = false;
let RESTORATION_IN_PROGRESS = false;
let RESTORATION_COMPLETED = false;

export function setHomeButtonCentering(value: boolean) {
  HOME_BUTTON_CENTERING = value;
}

export function setRestorationCompleted(value: boolean) {
  RESTORATION_COMPLETED = value;
}

let core: Core;

export function storePanZoomDynamic(cy: Core) {
  if (!cy) return;
  core = cy;
  core.on('pan zoom', panZoomListener);
  IS_PAN_ZOOM_DESTROYED = false;
  RESTORATION_COMPLETED = false;
}

export function destroyPanZoom(cy: Core) {
  if (!cy) return;
  cy.off('pan zoom', panZoomListener);
  IS_PAN_ZOOM_DESTROYED = true;
}

export function restorePanZoomDynamic(cy?: Core, useAnimation = true): boolean {

  if (!cy) {
    return false;
  }

  core = cy;

  // Prevent multiple restoration calls in quick succession
  if (RESTORATION_IN_PROGRESS) {
    return false;
  }

  const stopRestorePanZoom = (success: boolean) => {
    setTimeout(() => {
      isRestoringPanZoom.set(false);
      RESTORATION_IN_PROGRESS = false;
      RESTORATION_COMPLETED = success;
    }, success ? 500 : 0); // No delay if restoration failed
  };

  const currentMapId = new URLSearchParams(window.location.search).get('mapId');
  const localStorageMapString = readSelectedMap() as string;
  const map = isInIframe() ? get(JSON.parse(localStorage.getItem(GraphNodeMapListKey) as string), currentMapId!) : JSON.parse(localStorageMapString !== 'undefined' ? localStorageMapString : '{}');
  if (!map || !map.id) {
    return false;
  }

  const panZoomStorageRaw = localStorage.getItem(PAN_ZOOM_STORAGE_KEY);

  const panZoomStorage = JSON.parse(panZoomStorageRaw as string) as PanZoomStorage[];

  if (!panZoomStorage) {
    invalidateGeneralPanZoom();
    invalidatePanZoomForCollapsedNode();
    return false;
  }

  // find the setting for the current map
  const currentMapPanZoom = panZoomStorage.find(p => p.map.id === map.id);

  if (!currentMapPanZoom) {
    invalidateGeneralPanZoom();
    invalidatePanZoomForCollapsedNode();
    return false;
  }


  // Only set flags if we actually have data to restore
  RESTORATION_IN_PROGRESS = true;
  isRestoringPanZoom.set(true);
  ZOOM_IN_PROGRESS = true;

  if (useAnimation) {
    core.animate({
      pan: currentMapPanZoom.pan,
      zoom: currentMapPanZoom.zoom,
      duration: 300,
      easing: 'ease-in-out',
      complete: () => {
        ZOOM_IN_PROGRESS = false;
        stopRestorePanZoom(true);
      }
    });
  } else {
    const clonedValues = structuredClone(currentMapPanZoom);

    // Delay 150ms to ensure style.update() and forceRender() (at 100ms) have completed.
    // The dashboard's 100ms setTimeout can interfere with immediate viewport restoration.
    setTimeout(() => {
      core.batch(() => {
        core.viewport({
          zoom: clonedValues.zoom,
          pan: clonedValues.pan
        });
      });
      ZOOM_IN_PROGRESS = false;
      stopRestorePanZoom(true);
    }, 150);
  }

  return true;
}

export function setPanZoomForCollapsedNode(nodeId: string, collapsed = false) {
  const pan = core.pan();
  const zoom = core.zoom();
  const state = {
    nodeId,
    pan,
    zoom,
    collapsed
  };
  localStorage.removeItem(GENERAL_COLLAPSED_PAN_STATE_KEY);
  localStorage.setItem(COLLAPSED_NODE_PAN_STATE_KEY, JSON.stringify(state));
}

export function setPanZoomGeneral() {
  const pan = core.pan();
  const zoom = core.zoom();
  const state = {
    pan,
    zoom,
  }
  invalidatePanZoomForCollapsedNode();
  localStorage.setItem(GENERAL_COLLAPSED_PAN_STATE_KEY, JSON.stringify(state));
}

export function restoreGeneralPanZoom(cy: Core) {
  try {
    const state = JSON.parse(localStorage.getItem(GENERAL_COLLAPSED_PAN_STATE_KEY) as string) as CollapsedNodePanState;
    if (!state) return null;
    return state;
  } catch (e) {
    return null;
  }
}

export function invalidateGeneralPanZoom() {
  localStorage.removeItem(GENERAL_COLLAPSED_PAN_STATE_KEY);
}

export function deletePanZoomState() {
  if (!core) return;

  const queryParams = new URLSearchParams(window.location.search);
  const mapId = queryParams.get('mapId');
  if (!mapId && isInIframe()) return;

  const map = isInIframe() ? MapService.getIFramedMap(mapId!) : JSON.parse(readSelectedMap() as string);
  if (!map) return;

  const existing = JSON.parse(localStorage.getItem(PAN_ZOOM_STORAGE_KEY) as string) as PanZoomStorage[];
  if (existing && existing.length) {
    const filteredStorage = existing.filter(e => e.map.id !== map.id);
    if (filteredStorage.length > 0) {
      localStorage.setItem(PAN_ZOOM_STORAGE_KEY, JSON.stringify(filteredStorage));
    } else {
      localStorage.removeItem(PAN_ZOOM_STORAGE_KEY);
    }
  }
}

export function restorePanZoomForCollapsedNode(nodeId: string): CollapsedNodePanState | null {
  try {
    const state = JSON.parse(localStorage.getItem(COLLAPSED_NODE_PAN_STATE_KEY) as string) as CollapsedNodePanState;
    if (!state) return null;
    if (state.nodeId !== nodeId) return null;
    return state;
  } catch (e) {
    return null;
  }
}

export function restoreSavedPanZoomForCollapsedNode(nodeId: string, cy: Core, complete?: (collapsed: boolean) => void) {
  ExpandCollapseInProgress.set(true);
  const state =  restorePanZoomForCollapsedNode(nodeId);
  if (state) {
    cy.animate({
      pan: state.pan,
      zoom: state.zoom,
      duration: 500,
      easing: 'ease-in-out',
      complete() {
        ExpandCollapseInProgress.set(false);
        if (complete) {
          complete(state.collapsed);
        }
      }
    });
  }
}

export function invalidatePanZoomForCollapsedNode() {
  localStorage.removeItem(COLLAPSED_NODE_PAN_STATE_KEY);
}

export const ExpandCollapseInProgress = signal(false);

function panZoomListener() {
  if (centeringInProgress() || ExpandCollapseInProgress()) {
    return;
  }

  // Block saves until restoration is completed
  if (!RESTORATION_COMPLETED) {
    return;
  }

  savePanZoomState();
}

export function savePanZoomState() {
  if (!core) {
    return;
  }

  // Don't save if home button centering is in progress
  if (HOME_BUTTON_CENTERING) {
    return;
  }

  // Don't save if restoration is in progress
  if (isRestoringPanZoom()) {
    return;
  }

  const currentPan = core.pan();
  const currentZoom = core.zoom();

  const queryParams = new URLSearchParams(window.location.search);
  const mapId = queryParams.get('mapId');
  if (!mapId && isInIframe()) {
    return;
  }

  const map = isInIframe() ? MapService.getIFramedMap(mapId!) : JSON.parse(readSelectedMap() as string);
  if (!map) {
    return;
  }

  const existing = JSON.parse(localStorage.getItem(PAN_ZOOM_STORAGE_KEY) as string) as PanZoomStorage[];
  if (existing && existing.length) {
    const existingMap = existing.find(e => e.map.id === map.id);
    if (existingMap) {
      existingMap.pan = core.pan();
      existingMap.zoom = core.zoom();
      localStorage.setItem(PAN_ZOOM_STORAGE_KEY, JSON.stringify(existing));
      return;
    } else {
      existing.push({
        pan: core.pan(),
        zoom: core.zoom(),
        map
      });
      localStorage.setItem(PAN_ZOOM_STORAGE_KEY, JSON.stringify(existing));
      return;
    }
  } else {
    localStorage.setItem(PAN_ZOOM_STORAGE_KEY, JSON.stringify([{
      pan: core.pan(),
      zoom: core.zoom(),
      map
    }]));
  }
}

export interface PanZoomStorage {
  pan: { x: number, y: number };
  zoom: number;
  map: GraphMap;
}

export interface CollapsedNodePanState {
  nodeId: string;
  pan: { x: number, y: number };
  zoom: number;
  collapsed: boolean;
}
